"use client"

import { useState, useContext, useEffect, useRef, useMemo } from "react"
import { ErpContext } from "../../context/ErpContext.js"
import {
  Button,
  Typography,
  Container,
  Modal,
  Box,
  CircularProgress,
  IconButton,
  Snackbar,
  Alert,
  Collapse,
  Chip,
  Grid,
  TextField,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Autocomplete,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import SaveIcon from "@mui/icons-material/Save"
import PersonIcon from "@mui/icons-material/Person"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import FormularioEntidadComponent from "./FormularioEntidadComponent.js"

const CrearEntidadClienteModalComponent = ({
  open,
  handleClose,
  entidad,
  isEditing,
  handleAlerta,
  onClienteCreado,
  tipoEntidad = "cliente",
  tipoEntidadReadonly = true,
  excluirIdTipoEntidad = [],
  tituloPersonalizado = null,
}) => {
  const { 
    tipoEntidades, 
    getTipoEntidad, 
    postEntidad, 
    updateEntidad, 
    condicionIva, 
    getCondicionIva, 
    getCondicionIvaByEntidad, 
    isNavbarCollapsed,
    entidadAtributoNoEliminados,
    getEntidadAtributoNoEliminados,
    getEntidadAtributoClasificacionByIdAtributo,
    getEntidadByID,
  } = useContext(ErpContext)

  // Estados para los campos del formulario
  const [descripcionEntidad, setDescripcionEntidad] = useState("")
  const [apellidoEntidad, setApellidoEntidad] = useState("")
  const [telefonoEntidad, setTelefonoEntidad] = useState("")
  const [emailEntidad, setEmailEntidad] = useState("")
  const [dniCuitCuilEntidad, setdniCuitCuilEntidad] = useState("")
  const [direccionEntidad, setDireccionEntidad] = useState("")
  const [localidadEntidad, setLocalidadEntidad] = useState("")
  const [provinciaEntidad, setProvinciaEntidad] = useState("")
  const [idTipoEntidadEntidad, setIdTipoEntidadEntidad] = useState("")
  const [idCondicionIvaEntidad, setIdCondicionIvaEntidad] = useState("")

  // Estados adicionales para UI
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingData, setIsLoadingData] = useState(false) // Loading para carga inicial de datos
  const [errors, setErrors] = useState({})
  const [notification, setNotification] = useState({ open: false, message: "", severity: "error" })
  const isFirstRender = useRef(true)
  const entidadCargadaRef = useRef(false) // Para evitar cargar múltiples veces
  
  // Estados para atributos
  const [atributosForm, setAtributosForm] = useState([])
  const [expandedAtributos, setExpandedAtributos] = useState(false)
  const [opcionesPorAtributo, setOpcionesPorAtributo] = useState({}) // { idAtributo: [opciones] }

  const toTitleCase = (texto = "") =>
    texto
      .toLowerCase()
      .replace(/(^|\s)\S/g, (char) => char.toUpperCase())

  const descripcionTipoEntidad = useMemo(() => {
    if (tipoEntidad && typeof tipoEntidad === "object") {
      return tipoEntidad.descripcion || "Entidad"
    }
    if (typeof tipoEntidad === "string") {
      return tipoEntidad || "Entidad"
    }
    return "Entidad"
  }, [tipoEntidad])

  const descripcionTitulo = useMemo(
    () => toTitleCase(descripcionTipoEntidad.trim() || "Entidad"),
    [descripcionTipoEntidad],
  )

  const tipoEntidadIdPredeterminado = useMemo(() => {
    if (tipoEntidad && typeof tipoEntidad === "object" && tipoEntidad.id) {
      return tipoEntidad.id
    }
    if (typeof descripcionTipoEntidad === "string" && descripcionTipoEntidad) {
      const coincidencia = tipoEntidades.find(
        (t) => t.descripcion?.toLowerCase() === descripcionTipoEntidad.toLowerCase(),
      )
      if (coincidencia) return coincidencia.id

      const mapaFallback = {
        cliente: 1,
        proveedor: 2,
      }
      return mapaFallback[descripcionTipoEntidad.toLowerCase()] || null
    }
    return null
  }, [tipoEntidad, descripcionTipoEntidad, tipoEntidades])

  const labels = useMemo(() => {
    const textoBase = tituloPersonalizado || `Crear Nuevo ${descripcionTitulo}`
    const textoActualizar = tituloPersonalizado
      ? tituloPersonalizado.replace(/^Crear/i, "Actualizar")
      : `Actualizar ${descripcionTitulo}`

    return {
      crear: textoBase,
      actualizar: textoActualizar,
    }
  }, [descripcionTitulo, tituloPersonalizado])

  const exclusionSet = useMemo(() => {
    if (!Array.isArray(excluirIdTipoEntidad)) return new Set()
    return new Set(excluirIdTipoEntidad)
  }, [excluirIdTipoEntidad])

  const tipoEntidadesFiltradas = useMemo(
    () =>
      tipoEntidades.filter(
        (t) => !exclusionSet.has(t.id) || t.id === tipoEntidadIdPredeterminado,
      ),
    [tipoEntidades, exclusionSet, tipoEntidadIdPredeterminado],
  )

  useEffect(() => {
    if (!open) {
      // Limpiar el estado cuando se cierra el modal
      resetForm()
      entidadCargadaRef.current = false
      isFirstRender.current = true
      setIsLoadingData(false)
      return
    }

    // Resetear flag cuando se abre el modal
    entidadCargadaRef.current = false

    // Cargar datos básicos necesarios
    const cargarDatosBasicos = async () => {
      const promises = []
      
      if (tipoEntidades.length === 0) {
        promises.push(getTipoEntidad())
      }

      if (condicionIva.length === 0) {
        promises.push(getCondicionIva())
      }

      // Cargar atributos de entidad cuando se abre el modal (solo si no están cargados)
      // Usar idTipoEntidad = 1 (Cliente) por defecto para este modal
      const idTipoEntidad = idTipoEntidadEntidad || entidad?.idTipoEntidad || 1
      if (!entidadAtributoNoEliminados || entidadAtributoNoEliminados.length === 0) {
        promises.push(getEntidadAtributoNoEliminados(idTipoEntidad))
      }

      await Promise.all(promises)
    }

    cargarDatosBasicos()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Cargar opciones para cada atributo
  const cargarOpcionesAtributos = async (idTipoEntidadParam = null) => {
    if (!entidadAtributoNoEliminados || entidadAtributoNoEliminados.length === 0) {
      setOpcionesPorAtributo({})
      return
    }

    // Usar el parámetro si se proporciona, sino usar el estado o el prop
    const idTipoEntidad = idTipoEntidadParam || idTipoEntidadEntidad || entidad?.idTipoEntidad || 1
    if (!idTipoEntidad) {
      console.warn('idTipoEntidad no disponible para cargar atributos')
      setOpcionesPorAtributo({})
      return
    }

    const opcionesMap = {}
    for (const attr of entidadAtributoNoEliminados) {
      try {
        const opciones = await getEntidadAtributoClasificacionByIdAtributo(attr.idEntidadAtributo, idTipoEntidad)
        opcionesMap[attr.idEntidadAtributo] = opciones || []
      } catch (error) {
        console.error(`Error al cargar opciones para atributo ${attr.idEntidadAtributo}:`, error)
        opcionesMap[attr.idEntidadAtributo] = []
      }
    }
    setOpcionesPorAtributo(opcionesMap)
  }

  // Cargar datos completos de la entidad cuando se abre en modo edición
  useEffect(() => {
    if (!open || !isEditing || !entidad?.id) return
    if (entidadCargadaRef.current) return // Evitar cargar múltiples veces

    const cargarDatosCompletos = async () => {
      setIsLoadingData(true)
      entidadCargadaRef.current = true

      try {
        // Cargar datos completos de la entidad desde el backend
        const entidadCompleta = await getEntidadByID(entidad.id)
        
        if (!entidadCompleta) {
          console.error("No se pudo cargar la entidad completa")
          setIsLoadingData(false)
          return
        }

        // Cargar atributos disponibles basados en el idTipoEntidad de la entidad
        const idTipoEntidad = entidadCompleta?.idTipoEntidad || 1
        
        // Siempre recargar los atributos para asegurar que sean del tipo correcto
        await getEntidadAtributoNoEliminados(idTipoEntidad)

        // Esperar a que los atributos estén disponibles
        let atributosDisponibles = entidadAtributoNoEliminados
        if (!atributosDisponibles || atributosDisponibles.length === 0) {
          // Esperar un poco y volver a intentar
          await new Promise(resolve => setTimeout(resolve, 200))
          atributosDisponibles = entidadAtributoNoEliminados
        }

        // Cargar opciones de atributos usando el idTipoEntidad de la entidad
        if (atributosDisponibles && atributosDisponibles.length > 0) {
          await cargarOpcionesAtributos(idTipoEntidad)
          
          // Inicializar atributos con los valores de la entidad completa
          const atributosInicializados = atributosDisponibles.map((attr, index) => {
            // Buscar el valor en la entidad completa (puede venir como opcionAtributoX o entidadDatoAtributoX)
            const opcionAtributo = entidadCompleta[`opcionAtributo${index + 1}`]
            const valorDirecto = entidadCompleta[`entidadDatoAtributo${index + 1}`]
            
            // Priorizar opcionAtributo si existe (tiene el objeto completo), sino usar valorDirecto
            const valor = opcionAtributo?.id || valorDirecto || null
            
            return {
              id: attr.idEntidadAtributo || attr.id, // Usar idEntidadAtributo si existe
              descripcion: attr.descripcion,
              valor: valor,
            }
          })
          setAtributosForm(atributosInicializados)
        }

        // Cargar datos básicos de la entidad
        setDescripcionEntidad(entidadCompleta.descripcion || "")
        setApellidoEntidad(entidadCompleta.apellido || "")
        setTelefonoEntidad(entidadCompleta.telefono || "")
        setEmailEntidad(entidadCompleta.email || "")
        setdniCuitCuilEntidad(entidadCompleta.dniCuitCuil || "")
        setDireccionEntidad(entidadCompleta.direccion || "")
        setLocalidadEntidad(entidadCompleta.localidad || "")
        setProvinciaEntidad(entidadCompleta.provincia || "")
        setIdTipoEntidadEntidad(entidadCompleta.idTipoEntidad || "")

        // Obtener la condición IVA desde el backend
        try {
          const condicionIvaData = await getCondicionIvaByEntidad(entidadCompleta.id)
          setIdCondicionIvaEntidad(condicionIvaData?.idCondicionIva || "")
        } catch (error) {
          console.error("Error obteniendo condición IVA:", error)
          setIdCondicionIvaEntidad("")
        }

      } catch (error) {
        console.error("Error al cargar datos completos de la entidad:", error)
        setNotification({
          open: true,
          message: "Error al cargar los datos de la entidad. Por favor, intente nuevamente.",
          severity: "error",
        })
      } finally {
        setIsLoadingData(false)
      }
    }

    cargarDatosCompletos()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEditing, entidad?.id])

  // Recargar atributos cuando cambia el idTipoEntidad (tanto en creación como en edición)
  useEffect(() => {
    if (!open || !idTipoEntidadEntidad) return
    
    const recargarAtributosPorTipo = async () => {
      try {
        // Cargar atributos del tipo de entidad seleccionado
        await getEntidadAtributoNoEliminados(idTipoEntidadEntidad)
        
        // Esperar un momento para que se actualicen los atributos
        await new Promise(resolve => setTimeout(resolve, 100))
        
        // Cargar opciones de clasificación para ese tipo
        await cargarOpcionesAtributos(idTipoEntidadEntidad)
        
        // Si estamos en modo creación, inicializar atributos vacíos
        if (!isEditing && entidadAtributoNoEliminados && entidadAtributoNoEliminados.length > 0) {
          const inicial = entidadAtributoNoEliminados.map((attr) => ({
            id: attr.idEntidadAtributo || attr.id, // Usar idEntidadAtributo si existe
            descripcion: attr.descripcion,
            valor: null,
          }))
          setAtributosForm(inicial)
        }
      } catch (error) {
        console.error('Error al recargar atributos por tipo:', error)
      }
    }
    
    recargarAtributosPorTipo()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idTipoEntidadEntidad, open])

  // Inicializar atributos cuando se cargan los atributos disponibles (solo para modo creación)
  useEffect(() => {
    if (!open || isEditing) return // Solo para modo creación
    
    const inicializarAtributos = async () => {
      if (entidadAtributoNoEliminados && entidadAtributoNoEliminados.length > 0) {
        // Cargar opciones primero usando el idTipoEntidad actual
        const idTipoEntidad = idTipoEntidadEntidad || 1
        await cargarOpcionesAtributos(idTipoEntidad)
        
        // Luego inicializar los valores como null (modo creación)
        const inicial = entidadAtributoNoEliminados.map((attr) => ({
          id: attr.idEntidadAtributo || attr.id, // Usar idEntidadAtributo si existe
          descripcion: attr.descripcion,
          valor: null,
        }))
        setAtributosForm(inicial)
      } else if (entidadAtributoNoEliminados && entidadAtributoNoEliminados.length === 0) {
        // Si no hay atributos, limpiar el formulario
        setAtributosForm([])
        setOpcionesPorAtributo({})
      }
    }
    
    inicializarAtributos()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEditing, entidadAtributoNoEliminados])

  // Limpiar formulario cuando se abre en modo creación
  useEffect(() => {
    if (!open) return
    
    if (!isEditing && !entidad) {
      // Modo creación: limpiar formulario
      resetForm()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEditing, entidad])

  // Función para validar el formulario
  const validateForm = () => {
    const newErrors = {}

    if (!descripcionEntidad.trim()) {
      newErrors.descripcion = "El nombre es requerido"
    }

    if (!dniCuitCuilEntidad.trim()) {
      newErrors.dniCuitCuil = "El DNI/CUIT/CUIL es requerido"
    }

    if (emailEntidad && !/\S+@\S+\.\S+/.test(emailEntidad)) {
      newErrors.email = "Email inválido"
    }

    if (!idTipoEntidadEntidad) {
      newErrors.idTipoEntidad = "Debe seleccionar un tipo de entidad"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Función para resetear el formulario
  const resetForm = () => {
    setDescripcionEntidad("")
    setApellidoEntidad("")
    setTelefonoEntidad("")
    setEmailEntidad("")
    setdniCuitCuilEntidad("")
    setDireccionEntidad("")
    setLocalidadEntidad("")
    setProvinciaEntidad("")
    setIdTipoEntidadEntidad("")
    setIdCondicionIvaEntidad("")
    setErrors({})
    // Resetear atributos
    if (entidadAtributoNoEliminados?.length > 0) {
      const resetAtributos = entidadAtributoNoEliminados.map((attr) => ({
        id: attr.id,
        descripcion: attr.descripcion,
        valor: null,
      }))
      setAtributosForm(resetAtributos)
    }
    setExpandedAtributos(false)
  }

  // Manejar cambio en atributos
  const handleAtributoFormChange = (id, valor) => {
    // Convertir string vacío a null
    const valorFinal = valor === "" || valor === null ? null : (typeof valor === 'string' ? parseInt(valor) || null : valor)
    setAtributosForm((prev) => prev.map((attr) => (attr.id === id ? { ...attr, valor: valorFinal } : attr)))
  }

  const handleEntidadSubmit = async (e) => {
    e.preventDefault()
    e.stopPropagation() // Evita que el evento se propague al formulario padre

    // Validar formulario
    if (!validateForm()) {
      return
    }

    try {
      setIsLoading(true)

      if (isEditing) {
            // Validar que no se intente actualizar entidades genéricas
            if (entidad.id === 1 || entidad.id === 2) {
              setNotification({
                open: true,
                message: "No se puede actualizar entidades genéricas",
                severity: "warning",
              });
              setIsLoading(false);
              return;
            }

        // Si es una edición, actualiza la entidad existente
        await updateEntidad(
          entidad.id,
          descripcionEntidad,
          apellidoEntidad,
          telefonoEntidad,
          emailEntidad,
          dniCuitCuilEntidad,
          direccionEntidad,
          localidadEntidad,
          provinciaEntidad,
          idTipoEntidadEntidad,
          idCondicionIvaEntidad,
          atributosForm,
        )
      } else {
        // Si no, crea una nueva entidad
        const clienteCreado = await postEntidad(
          descripcionEntidad,
          apellidoEntidad,
          telefonoEntidad,
          emailEntidad,
          dniCuitCuilEntidad,
          direccionEntidad,
          localidadEntidad,
          provinciaEntidad,
          idTipoEntidadEntidad,
          idCondicionIvaEntidad,
          atributosForm,
        )

        // Si se creó un cliente nuevo y existe la función callback, llamarla
        if (clienteCreado && onClienteCreado) {
          const clienteData = {
            id: clienteCreado.id,
            label: `${descripcionEntidad} ${apellidoEntidad} ${dniCuitCuilEntidad}`,
            descripcion: descripcionEntidad,
            apellido: apellidoEntidad,
            dniCuitCuil: dniCuitCuilEntidad,
            telefono: telefonoEntidad,
            email: emailEntidad,
            direccion: direccionEntidad,
            localidad: localidadEntidad,
            provincia: provinciaEntidad,
            idTipoEntidad: idTipoEntidadEntidad,
            idCondicionIva: idCondicionIvaEntidad,
          }
          onClienteCreado(clienteData)
        }
      }

      // Limpiar los campos después de la acción
      resetForm()
      handleAlerta(true)
      handleClose()
    } catch (error) {
      console.error("Error al procesar entidad:", error)
      
     // Mostrar mensaje de error al usuario
      setNotification({
        open: true,
        message: error.message || "Ocurrió un error al procesar la solicitud. Por favor, intente nuevamente.",
        severity: "error",
      });
      
      handleAlerta(false)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal open={open} onClose={handleClose}>
       <Box>
      <div
        className="modalContenido"
        style={{
          backgroundColor: "white",
          borderRadius: "16px",
          padding: "0",
          width: "80%",
          maxWidth: "800px",
          maxHeight: "90vh",
          overflow: "auto",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)",
        }}
      >
        {/* Header */}
        <div
          style={{
            backgroundColor: "#5b5b5f",
            color: "white",
            padding: "16px 24px",
            borderTopLeftRadius: "16px",
            borderTopRightRadius: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <PersonIcon style={{ marginRight: "12px", color: "rgb(244, 177, 14)" }} />
            <Typography variant="h6" style={{ fontWeight: "500" }}>
              {isEditing ? labels.actualizar : labels.crear}
            </Typography>
          </div>
          <IconButton onClick={handleClose} style={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </div>

        <Container className="contenedorModal containerModuloDiseñoGeneral" style={{ padding: "24px", position: "relative" }}>
          {/* Loading overlay para carga inicial de datos */}
          {isLoadingData && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 1000,
                borderRadius: "0 0 16px 16px",
                gap: 2,
              }}
            >
              <CircularProgress size={60} thickness={4} sx={{ color: "#5b5b5f" }} />
              <Typography variant="body1" sx={{ color: "#5b5b5f", fontWeight: 500 }}>
                Cargando datos de la entidad...
              </Typography>
            </Box>
          )}
          
          <form onSubmit={handleEntidadSubmit}>
            <FormularioEntidadComponent
              descripcionEntidad={descripcionEntidad}
              setDescripcionEntidad={setDescripcionEntidad}
              apellidoEntidad={apellidoEntidad}
              setApellidoEntidad={setApellidoEntidad}
              dniCuitCuilEntidad={dniCuitCuilEntidad}
              setdniCuitCuilEntidad={setdniCuitCuilEntidad}
              telefonoEntidad={telefonoEntidad}
              setTelefonoEntidad={setTelefonoEntidad}
              emailEntidad={emailEntidad}
              setEmailEntidad={setEmailEntidad}
              direccionEntidad={direccionEntidad}
              setDireccionEntidad={setDireccionEntidad}
              localidadEntidad={localidadEntidad}
              setLocalidadEntidad={setLocalidadEntidad}
              provinciaEntidad={provinciaEntidad}
              setProvinciaEntidad={setProvinciaEntidad}
              idTipoEntidadEntidad={idTipoEntidadEntidad}
              setIdTipoEntidadEntidad={setIdTipoEntidadEntidad}
              idCondicionIvaEntidad={idCondicionIvaEntidad}
              setIdCondicionIvaEntidad={setIdCondicionIvaEntidad}
              tipoEntidades={tipoEntidadesFiltradas}
              condicionIva={condicionIva}
              errors={errors}
              showCondicionIva={true}
              compact={true}
              tipoEntidadReadonly={tipoEntidadReadonly}
              tipoEntidadPredeterminado={tipoEntidadIdPredeterminado}
              disabled={isLoadingData}
            />

            {/* Collapse para Atributos */}
            {entidadAtributoNoEliminados && entidadAtributoNoEliminados.length > 0 && (
              <Box sx={{ mt: 3, opacity: isLoadingData ? 0.5 : 1, pointerEvents: isLoadingData ? "none" : "auto" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: isLoadingData ? "not-allowed" : "pointer",
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor: "#f5f5f5",
                    "&:hover": {
                      backgroundColor: isLoadingData ? "#f5f5f5" : "#eeeeee",
                    },
                  }}
                  onClick={() => !isLoadingData && setExpandedAtributos(!expandedAtributos)}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Divider sx={{ flex: 1, maxWidth: 200 }}>
                      <Chip 
                        label="Atributos de la Entidad" 
                        variant="outlined" 
                        sx={{ 
                          fontWeight: 500, 
                          color: '#5b5b5f',
                          border: '1px solid #5b5b5f',
                        }} 
                      />
                    </Divider>
                  </Box>
                  <IconButton size="small">
                    {expandedAtributos ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
                <Collapse in={expandedAtributos} timeout="auto" unmountOnExit>
                  <Grid container spacing={2} sx={{ mt: 1, p: 2 }}>
                    {atributosForm.map((attr) => {
                      const opciones = opcionesPorAtributo[attr.id] || []
                      return (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={attr.id}>
                          <Autocomplete
                            size="small"
                            disablePortal
                            options={opciones}
                            getOptionLabel={(option) => option.descripcion || ""}
                            value={opciones.find(o => o.id === attr.valor) || null}
                            onChange={(event, newValue) => {
                              handleAtributoFormChange(attr.id, newValue ? newValue.id : null)
                            }}
                            disabled={isLoadingData}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={attr.descripcion}
                                sx={{
                                  borderRadius: 2,
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "#5b5b5f",
                                    },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "#5b5b5f",
                                    },
                                  },
                                  "& .MuiInputLabel-root.Mui-focused": {
                                    color: "#5b5b5f",
                                  },
                                }}
                              />
                            )}
                          />
                        </Grid>
                      )
                    })}
                  </Grid>
                </Collapse>
              </Box>
            )}

            {/* Botones de acción */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 4,
                gap: 2,
              }}
            >
              <Button
                variant="outlined"
                onClick={handleClose}
                disabled={isLoading || isLoadingData}
                style={{
                  padding: "10px 24px",
                  borderRadius: "8px",
                  fontWeight: "500",
                  borderWidth: "2px",
                  borderColor: "#5b5b5f",
                  color: "#5b5b5f",
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                startIcon={isLoading || isLoadingData ? null : <SaveIcon />}
                disabled={isLoading || isLoadingData}
                style={{
                  padding: "10px 24px",
                  borderRadius: "8px",
                  fontWeight: "500",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                  backgroundColor: "#5b5b5f",
                }}
              >
                {isLoading || isLoadingData ? (
                  <CircularProgress size={24} color="inherit" />
                ) : isEditing ? (
                  "Actualizar"
                ) : (
                  "Guardar"
                )}
              </Button>
            </Box>
          </form>
        </Container>

        {isLoading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 10,
              borderRadius: "16px",
            }}
          >
            <CircularProgress size={60} thickness={4} />
          </div>
        )}
      </div>

{/* Snackbar para notificaciones */}
<Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          variant="filled"
          sx={{
            width: "100%",
            "& .MuiAlert-icon": {
              fontSize: "1.5rem",
            },
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
      </Box>      
    </Modal>
  )
}

export default CrearEntidadClienteModalComponent
